import type { ServiceDiagnosticsBundle } from '@web/vin-sessions/types';

import { getLogger } from '@packages/core/logging';
import { docsService } from '@web/services/singletons';
import { useCallback, useEffect, useState } from 'react';

import type { SdFilters, SdSearchType, SdSort } from '../sd-search.utils';

const logger = getLogger('service-diagnostics');

export type SdBundleSearch = {
    bundles: ServiceDiagnosticsBundle[];
    hasFailed: boolean;
    /** True once a keyword is being searched, which is what fills in the match counts. */
    isKeywordSearch: boolean;
    isLoading: boolean;
    refresh: () => void;
    setFavorite: (documentId: string, isFavorite: boolean) => void;
    totalElements: number;
};

type Params = {
    /** Nothing is fetched until the account is known, since the tabs are per account. */
    enabled: boolean;
    filters: SdFilters;
    page: number;
    pageSize: number;
    /** Column and direction while browsing. Ignored by the API once a keyword is searched. */
    sort: SdSort;
    /** The keyword already submitted with Search, not what sits in the box. */
    query: string;
    searchType: SdSearchType;
};

export const useSdBundleSearch = ({
    query,
    searchType,
    filters,
    page,
    pageSize,
    sort,
    enabled,
}: Params): SdBundleSearch => {
    const [bundles, setBundles] = useState<ServiceDiagnosticsBundle[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const trimmedQuery = query.trim();

    useEffect(() => {
        if (!enabled) {
            return;
        }
        const controller = new AbortController();
        setIsLoading(true);
        docsService
            .searchServiceDiagnosticsBundles({
                queryParameters: {
                    ...(trimmedQuery ? { query: trimmedQuery } : {}),
                    searchType,
                    ...(filters.carlineId ? { carlineId: filters.carlineId } : {}),
                    ...(filters.modelYear ? { modelYear: Number(filters.modelYear) } : {}),
                    page,
                    size: pageSize,
                    ...(trimmedQuery ? {} : { sort: `${sort.column},${sort.direction}` }),
                },
                signal: controller.signal,
            })
            .then((response) => {
                if (controller.signal.aborted) {
                    return;
                }
                if (response.success) {
                    setBundles(response.data.content ?? []);
                    setTotalElements(response.data.page?.totalElements ?? 0);
                    setHasFailed(false);
                } else {
                    setHasFailed(true);
                }
            })
            .catch((error) => {
                if (controller.signal.aborted) {
                    return;
                }
                setHasFailed(true);
                logger.error('Failed to fetch the Service Diagnostics manuals', { error });
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });

        return () => controller.abort();
    }, [
        enabled,
        trimmedQuery,
        searchType,
        filters.carlineId,
        filters.modelYear,
        page,
        pageSize,
        sort.column,
        sort.direction,
        reloadToken,
    ]);

    /** Paints the star immediately; the list is refetched only when the tab depends on it. */
    const setFavorite = useCallback((documentId: string, isFavorite: boolean) => {
        setBundles((previous) =>
            previous.map((bundle) => (bundle.documentId === documentId ? { ...bundle, isFavorite } : bundle))
        );
    }, []);

    const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

    return {
        bundles,
        totalElements,
        // Not started is not the same as no results: without this the screen renders its empty
        // state until the account arrives, and Favorites claims the user has none.
        isLoading: !enabled || isLoading,
        hasFailed,
        isKeywordSearch: Boolean(trimmedQuery),
        setFavorite,
        refresh,
    };
};
